import Header from '../Header2';
import { useState, useEffect } from 'react';
import back_arrow from "../../assets/svg/back-arrow.svg";
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import '../../styles/FinancialCreation.css'
import delete_icon from '../../assets/svg/delete_icon.svg'
import { ToastContainer, toast } from "react-toastify";
import axiosInstance from '../../axiosInstance';
import AsyncSelect from "react-select/async";
import { useSelector, useDispatch } from "react-redux";
import React from "react";
import { PDFDownloadLink } from '@react-pdf/renderer';
import PdfGenerate from '../../ProjectCreation/PdfGenerate/PdfGenerate';
import CommonFooter from '../../CommonFooter/CommonFooter';

const FinancialCreation = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const periods = useSelector((state) => state.period.periods);
  const [submissionData, setSubmissionData] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const financialAssumptionDataId = location.state?.financialAssumptionId;
  const financialData = location.state?.financialData;
  const costid = location.state?.costId || financialData?.costId;
  const [costLineStructures, setCostLineStructures] = useState([]);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [activeCategoryContext, setActiveCategoryContext] = useState({
    categoryIndex: null,
    itemIndex: null,
    field: null
  });
  const initialFormData = {
    projectId: '',
    interestPerMonth: '',
    insurancePerMonth: '',
    capex: '',
    categories: [],
    costEscalations: []
  };

  const [formData, setFormData] = useState(initialFormData);

  const [pdfProjectDetails, setPdfProjectDetails] = useState(null);

  const [pdfPeriods, setPdfPeriods] = useState([]);

  useEffect(() => {
    const fetchProjectDetailsForPdf = async () => {
      if (formData.projectId) {
        try {
          const projectRes = await axiosInstance.get(`/costsolution/costlines/getprojects/${formData.projectId}`);
          setPdfProjectDetails(projectRes.data.projects);
          
          const periodRes = await axiosInstance.get(`/costsolution/costlines/getperiods/${formData.projectId}`);
          const periodsData = periodRes.data.ProjectPeriod || [];
          setPdfPeriods(periodsData);
        } catch (error) {
          console.error("Error fetching project details for PDF:", error);
        }
      }
    };

    fetchProjectDetailsForPdf();
  }, [formData.projectId]);


  useEffect(() => {
    if (location.state?.financialData) {
      const { financialData, isEditMode } = location.state;

      // Set project
      setSelectedProject({
        value: financialData.projectId,
        label: financialData.projectName || financialData.project?.projectName
      });

      // Format categories - properly merge cost categories with line items
      const formattedCategories = (financialData.categories || []).map(category => {
        // Find all cost category items for this category
        const categoryItems = (financialData.costCategories || [])
          .filter(cc => cc.categoryId === category.categoryId)
          .map((item, index) => ({
            id: item.id || Date.now() + index,
            sno: index + 1,
            elementName: item.elementId ? {
              value: item.elementId,
              label: item.element_name || item["element_name "] || ""
            } : null,
            costType: item.costId ? {
              value: item.costId,
              label: item.costType || ""
            } : null,
            value: item.value?.toString() || '',
            isChecked: Boolean(item.changeCostType)
          }));

        // If no items exist, create a default empty row
        const items = categoryItems.length > 0 ? categoryItems : [{
          id: Date.now(),
          sno: 1,
          elementName: null,
          costType: null,
          value: '',
          isChecked: false
        }];

        return {
          categoryId: category.categoryId,
          categoryName: category.categoryName,
          categoryCode: category.categoryCode,
          cost_id: category.cost_id,
          period_id: category.period_id,
          isExpanded: true,
          items: items
        };
      });

      // Format cost escalations (this part is working correctly)
      const formattedEscalations = (financialData.costEscalations || []).map(escalation => {
        const matchingCategory = (financialData.categories || []).find(
          cat => cat.categoryId === escalation.category_id
        );

        return {
          categoryId: escalation.category_id,
          categoryName: matchingCategory?.categoryName || '',
          percentage: escalation.percentage?.toString() || '0',
          interestPerMonth: escalation.interestPerMonth?.toString() || '0',
          insurancePerMonth: escalation["insurancePerMonth "]?.toString() || escalation.insurancePerMonth?.toString() || '0',
          marginRate: (escalation.marginRate !== undefined ? escalation.marginRate : escalation.margin_rate)?.toString() || '0',
          wiRate: (escalation.wiRate !== undefined ? escalation.wiRate : escalation.wi_rate)?.toString() || '0',
          bufferRate: (escalation.bufferRate !== undefined ? escalation.bufferRate : escalation.buffer_rate)?.toString() || '0'
        };
      });

      setFormData({
        projectId: financialData.projectId,
        interestPerMonth: financialData.interestPerMonth || '',
        insurancePerMonth: financialData.insurancePerMonth || financialData["insurancePerMonth "] || '',
        capex: financialData.capex || '',
        categories: formattedCategories,
        costEscalations: formattedEscalations
      });

      setIsEditing(isEditMode);
    }
  }, [location.state]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleProjectSelect = async (selectedOption) => {
    setSelectedProject(selectedOption);
    setFormData(prev => ({
      ...prev,
      projectId: selectedOption ? selectedOption.value : '',
      categories: [], // Clear existing categories when project changes
      costEscalations: []
    }));

    if (selectedOption) {
      try {
        // Fetch categories for the selected project
        const response = await axiosInstance.get(
          `/costsolution/costlines/getcategory/${selectedOption.value}`
        );

        if (response.data.success && response.data.projectcategory) {
          const projectCategories = response.data.projectcategory || [];

          // Initialize categories with empty items
          const initializedCategories = projectCategories.map(category => ({
            categoryId: category.categoryId,
            categoryName: category.categoryName,
            categoryCode: category.categoryCode,
            cost_id: category.cost_id,
            period_id: category.period_id,
            isExpanded: true, // Default to expanded
            items: [{
              id: Date.now() + Math.random(),
              sno: 1,
              elementName: null,
              costType: null,
              value: '',
              isChecked: false
            }]
          }));

          // Initialize cost escalations for each category
          const initialEscalations = projectCategories.map(category => ({
            categoryId: category.categoryId,
            categoryName: category.categoryName,
            percentage: '',
            interestPerMonth: '',
            insurancePerMonth: '',
            marginRate: '',
            wiRate: '',
            bufferRate: ''
          }));

          setFormData(prev => ({
            ...prev,
            projectId: selectedOption.value,
            categories: initializedCategories,
            costEscalations: initialEscalations
          }));
        } else {
          toast.error(response.data.message || "Failed to load project categories");
        }
      } catch (error) {
        console.error("Error fetching project categories:", error);
        toast.error("Failed to load project categories");
      }
    }
  };

  const loadProjectOptions = async (inputValue, callback) => {
    try {
      const res = await axiosInstance.get(
        "/costsolution/projects/autosuggestion",
        { params: { term: inputValue || '' } }
      );

      const projects = res.data?.autosuggestion || [];
      const mapped = projects.map((item) => ({
        label: item.label,
        value: item.value,
      }));

      callback(mapped);
    } catch (error) {
      console.error("Project suggestion error:", error);
      callback([]);
    }
  };

  const loadCostElementOptions = async (inputValue, callback) => {
    try {
      // Get categoryId from the active context
      const categoryId = activeCategoryContext.categoryIndex !== null
        ? formData.categories[activeCategoryContext.categoryIndex]?.categoryId
        : null;

      if (!categoryId) {
        console.error("No category selected or categoryId not found");
        callback([]);
        return;
      }

      const res = await axiosInstance.get(
        `/costsolution/costelement/autosuggestion`,
        {
          params: {
            term: inputValue || '',
            categoryId: categoryId
          }
        }
      );

      const costelement = res.data?.autosuggestion || [];
      const mapped = costelement.map((item) => ({
        label: item.label,
        value: item.value,
      }));

      callback(mapped);
    } catch (error) {
      console.error("Cost element suggestion error:", error);
      callback([]);
    }
  };

  const loadCostTypeOptions = async (inputValue, callback) => {
    try {
      // Get categoryId from the active context
      const categoryId = activeCategoryContext.categoryIndex !== null
        ? formData.categories[activeCategoryContext.categoryIndex]?.categoryId
        : null;

      if (!categoryId) {
        console.error("No category selected or categoryId not found");
        callback([]);
        return;
      }

      const res = await axiosInstance.get(
        `/costsolution/costtype/autosuggestion`,
        {
          params: {
            term: inputValue || '',
            categoryId: categoryId
          }
        }
      );

      const costtype = res.data?.costtype || [];
      const mapped = costtype.map((item) => ({
        label: `${item.name} (${item.code})`,
        value: item.id,
        fullItem: item
      }));

      callback(mapped);
    } catch (error) {
      console.error("Cost type suggestion error:", error);
      callback([]);
    }
  };


  const handleCategoryItemChange = (categoryIndex, itemIndex, field, value) => {
    setFormData(prev => {
      const updatedCategories = [...prev.categories];
      const updatedItems = [...updatedCategories[categoryIndex].items];

      updatedItems[itemIndex] = {
        ...updatedItems[itemIndex],
        [field]: value
      };

      updatedCategories[categoryIndex] = {
        ...updatedCategories[categoryIndex],
        items: updatedItems
      };

      return { ...prev, categories: updatedCategories };
    });
  };

  const handleEscalationChange = (index, value, fieldName) => {
    setFormData(prev => {
      const updatedEscalations = [...prev.costEscalations];
      updatedEscalations[index] = {
        ...updatedEscalations[index],
        [fieldName]: value
      };
      return { ...prev, costEscalations: updatedEscalations };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate required fields
    if (!formData.projectId) {
      toast.error("Please select a project");
      return;
    }

    try {
      // Prepare the data for submission
      const submissionData = {
        project_id: formData.projectId,
        costCategories: formData.categories.flatMap(category =>
          category.items.map(item => ({
            categoryId: category.categoryId,
            costId: item.costType?.value || 0,
            elementId: item.elementName?.value || 0,
            value: item.value || 0,
            changeCostType: item.isChecked || false,
          }))
        ),

        costEscalation: formData.costEscalations.map(escalation => ({
          categoryId: escalation.categoryId,
          percentage: escalation.percentage || '0',
          interestPerMonth: escalation.interestPerMonth || '0',
          insurancePerMonth: escalation.insurancePerMonth || '0',
          marginRate: escalation.marginRate || '0',
          wiRate: escalation.wiRate || '0',
          bufferRate: escalation.bufferRate || '0'
        }))
      };

      let response;
      if (isEditing) {
        // Update existing financial assumption
        response = await axiosInstance.put(
          `/costsolution/financial/${financialAssumptionDataId}`,
          submissionData
        );
      } else {
        // Create new financial assumption
        response = await axiosInstance.post(
          `/costsolution/financial`,
          submissionData
        );
      }

      if (response.data.success) {
        toast.success(
          isEditing
            ? "Financial assumption updated successfully!"
            : "Financial assumption created successfully!"
        );
        setSubmissionData(submissionData);

        try {
          const costlineRes = await axiosInstance.get(`/costsolution/costlines`, {
            params: { limit: 1000, skip: 0 }
          });
          const costlines = costlineRes.data?.costline || [];
          const hasCostline = costlines.some(item => String(item.projectId) === String(formData.projectId));

          if (hasCostline) {
            setShowConfirmation(true);
          } else {
            setTimeout(() => {
              navigate("/financialAssumption");
            }, 1000);
          }
        } catch (err) {
          console.error("Error checking costline presence:", err);
          setTimeout(() => {
            navigate("/financialAssumption");
          }, 1000);
        }
      } else {
        toast.error(response.data.message || "Error processing request");
      }
    } catch (error) {
      console.error("Error:", error);
      toast.error(error.response?.data?.message || "Something went wrong!");
    }
  };

  const toggleCategory = (categoryIndex) => {
    setFormData(prev => {
      const updatedCategories = [...prev.categories];
      updatedCategories[categoryIndex] = {
        ...updatedCategories[categoryIndex],
        isExpanded: !updatedCategories[categoryIndex].isExpanded
      };
      return { ...prev, categories: updatedCategories };
    });
  };

  const addNewRow = (categoryIndex) => {
    setFormData(prev => {
      const updatedCategories = [...prev.categories];
      const currentItems = updatedCategories[categoryIndex].items;

      const newItem = {
        id: Date.now(),
        sno: currentItems.length + 1,
        elementName: null,
        costType: null,
        value: '',
        isChecked: false
      };

      updatedCategories[categoryIndex] = {
        ...updatedCategories[categoryIndex],
        items: [...currentItems, newItem]
      };

      return { ...prev, categories: updatedCategories };
    });
  };

  const removeRow = (categoryIndex, itemId) => {
    setFormData(prev => {
      const updatedCategories = [...prev.categories];
      const categoryItems = updatedCategories[categoryIndex].items;

      if (categoryItems.length === 1) {
        toast.error("At least one row must be present in each category.");
        return prev;
      }

      updatedCategories[categoryIndex].items = categoryItems
        .filter(item => item.id !== itemId)
        .map((item, index) => ({ ...item, sno: index + 1 }));

      return { ...prev, categories: updatedCategories };
    });
  };



  const handleUpdateCostlines = async () => {
    if (!submissionData) {
      setShowConfirmation(false);
      navigate("/financialAssumption");
      return;
    }

    try {
      // Prepare the costline updates
      const costCategories = submissionData.costCategories.map(item => ({
        project_id: submissionData.project_id,
        cost_element_id: item.elementId,
        cost_type_code: item.costId,
        value: item.value
      }));

      // Call API to update costlines
      const response = await axiosInstance.post(
        `/costsolution/financial/recalculate`,
        { costcategories: costCategories }
      );

      if (response.data.success) {
        toast.success("Costlines updated successfully!");
      } else {
        toast.error(response.data.message || "Failed to update some costlines");
      }

    } catch (error) {
      console.error("Error updating costlines:", error);
      toast.error("Failed to update costlines");
    } finally {
      setShowConfirmation(false);
      navigate("/financialAssumption");
    }
  };

  useEffect(() => {
    const fetchCostLineStructures = async () => {
      if (!costid) return;

      try {
        const response = await axiosInstance.get(
          `/costsolution/costlinestructures/${costid}`
        );
        if (response.data.success) {
          setCostLineStructures(response.data.costlinestructure || []);
        }
      } catch (error) {
        console.error("Error fetching cost line structures:", error);
      }
    };

    fetchCostLineStructures();
  }, [costid]);


  const summaryData = {
    withBufferMonth: {
      rows: formData.categories.map(category => {
        // Initialize periods based on the actual periods found in costlinestructure
        const uniquePeriods = [...new Set(costLineStructures.map(line => line.period))];
        const periodValues = {};

        uniquePeriods.forEach(period => {
          periodValues[`period${period}`] = 0;
        });

        // Filter cost lines for this category
        const categoryCostLines = costLineStructures.filter(
          line => line.categoryId === category.categoryId
        );

        // Sum values for each period
        categoryCostLines.forEach(line => {
          const periodKey = `period${line.period}`;
          periodValues[periodKey] += parseFloat(line.costValueWithBufferMonth || 0);
        });

        // Calculate total
        const total = Object.values(periodValues).reduce(
          (sum, val) => sum + parseFloat(val || 0), 0
        ).toFixed(2);

        return {
          subcategory: category.categoryName,
          ...periodValues,
          total
        };
      })
    },
    // Repeat the same pattern for other tables (withoutBufferMonth, withBufferPeriod, withoutBufferPeriod)
    withoutBufferMonth: {
      rows: formData.categories.map(category => {
        const uniquePeriods = [...new Set(costLineStructures.map(line => line.period))];
        const periodValues = {};

        uniquePeriods.forEach(period => {
          periodValues[`period${period}`] = 0;
        });

        const categoryCostLines = costLineStructures.filter(
          line => line.categoryId === category.categoryId
        );

        categoryCostLines.forEach(line => {
          const periodKey = `period${line.period}`;
          periodValues[periodKey] += parseFloat(line.costValueWithoutBufferMonth || 0);
        });

        const total = Object.values(periodValues).reduce(
          (sum, val) => sum + parseFloat(val || 0), 0
        ).toFixed(2);

        return {
          subcategory: category.categoryName,
          ...periodValues,
          total
        };
      })
    },
    withBufferPeriod: {
      rows: formData.categories.map(category => {
        const uniquePeriods = [...new Set(costLineStructures.map(line => line.period))];
        const periodValues = {};

        uniquePeriods.forEach(period => {
          periodValues[`period${period}`] = 0;
        });

        const categoryCostLines = costLineStructures.filter(
          line => line.categoryId === category.categoryId
        );

        categoryCostLines.forEach(line => {
          const periodKey = `period${line.period}`;
          periodValues[periodKey] += parseFloat(line.costValueWithBufferPeriod || 0);
        });

        const total = Object.values(periodValues).reduce(
          (sum, val) => sum + parseFloat(val || 0), 0
        ).toFixed(2);

        return {
          subcategory: category.categoryName,
          ...periodValues,
          total
        };
      })
    },
    withoutBufferPeriod: {
      rows: formData.categories.map(category => {
        const uniquePeriods = [...new Set(costLineStructures.map(line => line.period))];
        const periodValues = {};

        uniquePeriods.forEach(period => {
          periodValues[`period${period}`] = 0;
        });

        const categoryCostLines = costLineStructures.filter(
          line => line.categoryId === category.categoryId
        );

        categoryCostLines.forEach(line => {
          const periodKey = `period${line.period}`;
          periodValues[periodKey] += parseFloat(line.costValueWithoutBufferPeriod || 0);
        });

        const total = Object.values(periodValues).reduce(
          (sum, val) => sum + parseFloat(val || 0), 0
        ).toFixed(2);

        return {
          subcategory: category.categoryName,
          ...periodValues,
          total
        };
      })
    }
  };

  const CostTable = ({ data, title }) => {
    // Get the actual periods from the data
    const periods = Object.keys(data.rows[0] || {})
      .filter(key => key.startsWith('period'))
      .map(key => parseInt(key.replace('period', '')))
      .sort((a, b) => a - b);

    const periodTotals = {};

    periods.forEach(period => {
      periodTotals[`period${period}`] = 0;
    });
    periodTotals.total = 0;

    data.rows.forEach(row => {
      periods.forEach(period => {
        periodTotals[`period${period}`] += parseFloat(row[`period${period}`] || 0);
      });
      periodTotals.total += parseFloat(row.total || 0);
    });

    return (
      <div className="cost-table-container" style={{ margin: '0px' }}>
        <div className="category-header">
          <span>{title}</span>
        </div>
        <table className="cost-table">
          <thead>
            <tr>
              <th className="subcategory-header">Category</th>
              {periods.map(period => (
                <th key={`period-${period}`}>Period {period}</th>
              ))}
              <th>Total</th>
            </tr>
          </thead>
          <tbody>
            {data.rows.map((row, index) => (
              <tr key={index}>
                <td className="subcategory-cell">{row.subcategory}</td>
                {periods.map(period => (
                  <td key={`value-${period}`}>
                    {parseFloat(row[`period${period}`]).toLocaleString()}
                  </td>
                ))}
                <td>{parseFloat(row.total).toLocaleString()}</td>
              </tr>
            ))}
            <tr className="grand-total-row">
              <td>Grand Total</td>
              {periods.map(period => (
                <td key={`total-${period}`}>
                  {periodTotals[`period${period}`].toLocaleString()}
                </td>
              ))}
              <td>{periodTotals.total.toLocaleString()}</td>
            </tr>
          </tbody>
        </table>
      </div>
    );
  };

  const CostComparisonTable = ({ withData, withoutData, title }) => {
    // Get the actual periods from the data
    const periods = Object.keys(withData.rows[0] || {})
      .filter(key => key.startsWith('period'))
      .map(key => parseInt(key.replace('period', '')))
      .sort((a, b) => a - b);

    const withTotals = {};
    const withoutTotals = {};

    // Initialize totals for all periods
    periods.forEach(period => {
      withTotals[`period${period}`] = 0;
      withoutTotals[`period${period}`] = 0;
    });
    withTotals.total = 0;
    withoutTotals.total = 0;

    // Calculate totals for "with" data
    withData.rows.forEach(row => {
      periods.forEach(period => {
        withTotals[`period${period}`] += parseFloat(row[`period${period}`] || 0);
      });
      withTotals.total += parseFloat(row.total || 0);
    });

    // Calculate totals for "without" data
    withoutData.rows.forEach(row => {
      periods.forEach(period => {
        withoutTotals[`period${period}`] += parseFloat(row[`period${period}`] || 0);
      });
      withoutTotals.total += parseFloat(row.total || 0);
    });

    return (
      <div className="cost-table-container" style={{ margin: '0px' }}>
        <div className="category-header">
          <span>{title}</span>
        </div>
        <table className="cost-table">
          <thead>
            <tr>
              <th className="subcategory-header">Category</th>
              {periods.map(period => (
                <th key={`period-${period}`} colSpan="2">Period {period}</th>
              ))}
              <th colSpan="2">Total</th>
            </tr>
            <tr>
              <th></th>
              {periods.map(period => (
                <React.Fragment key={`header-${period}`}>
                  <th>With Buffer</th>
                  <th>Without Buffer</th>
                </React.Fragment>
              ))}
              <th>With Buffer</th>
              <th>Without Buffer</th>
            </tr>
          </thead>
          <tbody>
            {withData.rows.map((row, index) => (
              <tr key={index}>
                <td className="subcategory-cell">{row.subcategory}</td>
                {periods.map(period => (
                  <React.Fragment key={`values-${period}`}>
                    <td>
                      {parseFloat(row[`period${period}`]).toLocaleString()}
                    </td>
                    <td>
                      {parseFloat(withoutData.rows[index]?.[`period${period}`] || 0).toLocaleString()}
                    </td>
                  </React.Fragment>
                ))}
                <td>{parseFloat(row.total).toLocaleString()}</td>
                <td>{parseFloat(withoutData.rows[index]?.total || 0).toLocaleString()}</td>
              </tr>
            ))}
            <tr className="grand-total-row">
              <td>Grand Total</td>
              {periods.map(period => (
                <React.Fragment key={`totals-${period}`}>
                  <td>{withTotals[`period${period}`].toLocaleString()}</td>
                  <td>{withoutTotals[`period${period}`].toLocaleString()}</td>
                </React.Fragment>
              ))}
              <td>{withTotals.total.toLocaleString()}</td>
              <td>{withoutTotals.total.toLocaleString()}</td>
            </tr>
          </tbody>
        </table>
      </div>
    );
  };



  return (
    <>
      <Header onSearch={null} disableSearch={true} />
      <div className='financial-creation'>
        <div className='financial-creation-header'>
          <div className='financial-creation-left-header'>
            <img
              src={back_arrow}
              alt="Back Arrow"
              onClick={() => navigate(-1)}
              style={{ cursor: "pointer" }}
            />
            <p className="new_cost_line" style={{ fontSize: '14px' }}>
              {isEditing ? "Edit Financial Assumption" : "New Financial Assumption"}
            </p>
          </div>
          <div className='financial-creation-right-header'>
            {isEditing && (
              <PDFDownloadLink
                document={<PdfGenerate summaryData={summaryData} periods={pdfPeriods} projectDetails={pdfProjectDetails} />}
                fileName="costline_summary.pdf"
              >
                {({ blob, url, loading, error }) => {
                  console.log("🔍 PDFDownloadLink state:", { blob, url, loading, error });
                  return (
                    <button className="clear_btn" disabled={loading}>
                      {loading ? "Generating PDF..." : "PDF"}
                    </button>
                  );
                }}
              </PDFDownloadLink>
            )}
            <button className="cancel_btn" onClick={() => navigate(-1)}>Cancel</button>
            <button className="submit_btn" onClick={handleSubmit}> {isEditing ? "Update" : "Save"}</button>
          </div>
        </div>

        {showConfirmation && (
          <div className="confirmation-popup-overlay">
            <div className="confirmation-popup">
              <div className="confirmation-popup-content">
                <p>Do you want the financial assumption value to be reflected on the costline page as well?</p>
              </div>
              <div className="confirmation-popup-actions">
                <button
                  className="confirmation-popup-cancel-btn"
                  onClick={() => {
                    setShowConfirmation(false);
                    navigate("/financialAssumption");
                  }}
                >
                  No
                </button>
                <button
                  className="confirmation-popup-confirm-btn"
                  onClick={handleUpdateCostlines}
                >
                  Yes
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="financial-creation-project-details">
          <h2 className="financial-creation-project-details-title">PROJECT DETAILS</h2>

          <div className="financial-creation-search-container">
            <div className="financial-creation-search-label">Project</div>
            <div className="financial-creation-search-bar-wrapper">
              <AsyncSelect
                cacheOptions
                defaultOptions
                loadOptions={loadProjectOptions}
                onChange={handleProjectSelect}
                value={selectedProject}
                placeholder="Search project..."
                isClearable
                styles={{
                  control: (base) => ({
                    ...base,
                    minHeight: '34px',
                    borderRadius: '8px',
                    fontSize: '13px',
                    width: '380px',
                    borderColor: '#DCDFE4',
                    '&:hover': {
                      borderColor: '#454CF2'
                    }
                  }),
                  container: (base) => ({
                    ...base,
                    width: '380px'
                  }),
                  menu: (base) => ({
                    ...base,
                    zIndex: 999,
                    fontSize: '13px',
                    marginTop: '4px',
                    boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
                  })
                }}
              />
            </div>
          </div>

          <div className="financial-creation-cards-container">
            <div className="financial-creation-capex-rate-card">
              <div className="capex-row header">
                <div className="capex-cell capex-label-header"><strong>Category</strong></div>
                <div className="capex-cell"><strong>Cost Escalation (%)</strong></div>
                <div className="capex-cell"><strong>Insurance Per Year (%)</strong></div>
                <div className="capex-cell"><strong>Interest Per Month (%)</strong></div>
                <div className="capex-cell"><strong>Margin Rate (%)</strong></div>
                <div className="capex-cell"><strong>WI Rate (%)</strong></div>
                <div className="capex-cell"><strong>Buffer Rate (%)</strong></div>
              </div>

              {formData.categories.map((category, index) => (
                <div className="capex-row" key={category.categoryId}>
                  <div className="capex-cell capex-label">{category.categoryName}</div>
                  <div className="capex-cell">
                    <input
                      type="text"
                      value={formData.costEscalations[index]?.percentage || ''}
                      onChange={(e) => handleEscalationChange(index, e.target.value, 'percentage')}
                      className="capex-input"
                      style={{ margin: '0px', textAlign: 'center', fontSize: '13px' }}
                    />
                  </div>
                  <div className="capex-cell">
                    <input
                      type="text"
                      value={formData.costEscalations[index]?.insurancePerMonth || ''}
                      onChange={(e) => handleEscalationChange(index, e.target.value, 'insurancePerMonth')}
                      className="capex-input"
                      style={{ margin: '0px', textAlign: 'center', fontSize: '13px' }}
                    />
                  </div>
                  <div className="capex-cell">
                    <input
                      type="text"
                      value={formData.costEscalations[index]?.interestPerMonth || ''}
                      onChange={(e) => handleEscalationChange(index, e.target.value, 'interestPerMonth')}
                      className="capex-input"
                      style={{ margin: '0px', textAlign: 'center', fontSize: '13px' }}
                    />
                  </div>
                  <div className="capex-cell">
                    <input
                      type="text"
                      value={formData.costEscalations[index]?.marginRate || ''}
                      onChange={(e) => handleEscalationChange(index, e.target.value, 'marginRate')}
                      className="capex-input"
                      style={{ margin: '0px', textAlign: 'center', fontSize: '13px' }}
                    />
                  </div>
                  <div className="capex-cell">
                    <input
                      type="text"
                      value={formData.costEscalations[index]?.wiRate || ''}
                      onChange={(e) => handleEscalationChange(index, e.target.value, 'wiRate')}
                      className="capex-input"
                      style={{ margin: '0px', textAlign: 'center', fontSize: '13px' }}
                    />
                  </div>
                  <div className="capex-cell">
                    <input
                      type="text"
                      value={formData.costEscalations[index]?.bufferRate || ''}
                      onChange={(e) => handleEscalationChange(index, e.target.value, 'bufferRate')}
                      className="capex-input"
                      style={{ margin: '0px', textAlign: 'center', fontSize: '13px' }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="cost-management-container">
            <div className="financial-creation-cost-categories-section">
              <h2 className="financial-creation-section-title">COST CATEGORIES</h2>

              <div className="financial-creation-categories-list">
                {formData.categories.map((category, categoryIndex) => (
                  <div key={category.categoryId || categoryIndex} className="financial-creation-category-container">
                    <div
                      className="financial-creation-category-header"
                      onClick={() => toggleCategory(categoryIndex)}
                    >
                      <span className="financial-creation-category-name">{category.categoryName}</span>
                    </div>

                    {(!category.hasOwnProperty('isExpanded') || category.isExpanded) && (
                      <div className="financial-creation-category-content" style={{ padding: '10px', overflowX: 'auto', whiteSpace: 'nowrap', scrollbarWidth: 'none', OverflowStyle: 'none', WebkitOverflowScrolling: 'touch', scrollBehavior: 'smooth' }}>
                        <table className="financial-creation-category-table" style={{ margin: '0px' }}>
                          <thead>
                            <tr>
                              <th>S.NO</th>
                              <th>ELEMENT NAME</th>
                              <th>COST TYPE</th>
                              <th>VALUE</th>

                            </tr>
                          </thead>
                          <tbody>
                            {category.items.map((item, itemIndex) => (
                              <tr key={item.id}>
                                <td style={{ alignItems: 'center' }}>
                                  <span className="sno">{item.sno}</span>
                                </td>
                                <td>
                                  <div className="financial-creation-input-with-icon">
                                    <AsyncSelect
                                      cacheOptions
                                      defaultOptions
                                      loadOptions={loadCostElementOptions}
                                      onChange={(selectedOption) =>
                                        handleCategoryItemChange(
                                          categoryIndex,
                                          itemIndex,
                                          'elementName',
                                          selectedOption
                                        )
                                      }
                                      onFocus={() => setActiveCategoryContext({
                                        categoryIndex,
                                        itemIndex,
                                        field: 'elementName'
                                      })}
                                      value={item.elementName}
                                      placeholder="Search ..."
                                      isClearable
                                      styles={{
                                        control: (base) => ({
                                          ...base,
                                          minHeight: '34px',
                                          borderRadius: '8px',
                                          fontSize: '12px',
                                          borderColor: '#DCDFE4',
                                          '&:hover': {
                                            borderColor: '#454CF2'
                                          },
                                          width: '300px'
                                        }),
                                        container: (base) => ({
                                          ...base,
                                          width: '300px'
                                        }),
                                        menu: (base) => ({
                                          ...base,
                                          zIndex: 999,
                                          fontSize: '12px',
                                          marginTop: '4px',
                                          boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
                                        })
                                      }}
                                    />
                                  </div>
                                </td>
                                <td>
                                  <div className="cost-type-cell">
                                    <input
                                      style={{ fontSize: '13px' }}
                                      type="checkbox"
                                      checked={item.isChecked}
                                      onChange={(e) =>
                                        handleCategoryItemChange(
                                          categoryIndex,
                                          itemIndex,
                                          'isChecked',
                                          e.target.checked
                                        )
                                      }
                                      className="checkbox-input"
                                    />
                                    <div className="financial-creation-input-with-icon">
                                      <AsyncSelect
                                        cacheOptions
                                        defaultOptions
                                        loadOptions={loadCostTypeOptions}
                                        onChange={(selectedOption) =>
                                          handleCategoryItemChange(
                                            categoryIndex,
                                            itemIndex,
                                            'costType',
                                            selectedOption
                                          )
                                        }
                                        onFocus={() => setActiveCategoryContext({
                                          categoryIndex,
                                          itemIndex,
                                          field: 'costType'
                                        })}
                                        value={item.costType}
                                        placeholder="Search ..."
                                        isClearable
                                        styles={{
                                          control: (base) => ({
                                            ...base,
                                            minHeight: '34px',
                                            borderRadius: '8px',
                                            fontSize: '12px',
                                            width: '250px',
                                            borderColor: '#DCDFE4',
                                            '&:hover': {
                                              borderColor: '#454CF2'
                                            }
                                          }),
                                          container: (base) => ({
                                            ...base,

                                          }),
                                          menu: (base) => ({
                                            ...base,
                                            zIndex: 999,
                                            fontSize: '12px',
                                            marginTop: '4px',
                                            boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
                                          })
                                        }}
                                      />
                                    </div>
                                  </div>
                                </td>
                                <td>
                                  <div className="financial-creation-value-cell">
                                    <div className="value-input-container">
                                      <input
                                        type=""
                                        placeholder="Enter the Value"
                                        value={item.value}
                                        onChange={(e) =>
                                          handleCategoryItemChange(
                                            categoryIndex,
                                            itemIndex,
                                            'value',
                                            e.target.value
                                          )
                                        }
                                        className="value-input"
                                      />
                                    </div>
                                  </div>
                                </td>
                                <td style={{ position: "relative", border: 'none', padding: '0px' }}>
                                  <span>
                                    <img
                                      src={delete_icon}
                                      className="financial-creation-remove-btn"
                                      style={{ cursor: "pointer", color: "red", fontWeight: "bold" }}
                                      title="Remove Row"
                                      alt=''
                                      onClick={() => removeRow(categoryIndex, item.id)}
                                    />
                                  </span>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                          <tfoot>
                            <tr>
                              <td style={{ border: 'none', padding: '0px' }}>
                                <button
                                  className="financial-creation-add-btn"
                                  onClick={() => addNewRow(categoryIndex)}
                                >
                                  + Add Row
                                </button>
                              </td>
                            </tr>
                          </tfoot>
                        </table>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* <div className="cost-escalation-section">
              <h2 className="financial-creation-section-title">COST ESCALATION</h2>
              
              <div className="financial-creation-escalation-list">
                {formData.categories.map((category, index) => (
                  <div key={index} className="financial-creation-escalation-row">
                    <span className="financial-creation-escalation-category">{category.categoryName}</span>
                    <span className="financial-creation-escalation-dash">-</span>
                    <div className="financial-creation-escalation-input-wrapper">
                      <input
                        type="number"
                        value={formData.costEscalations[index]?.percentage || ''}
                        onChange={(e) => handleEscalationChange(index, e.target.value)}
                        className="financial-creation-escalation-input-with-symbol"
                      />
                      <span className="financial-creation-input-percent-symbol">%</span>
                    </div>
                  </div>
                ))}
              </div>
            </div> */}
          </div>

          {isEditing && (
            <div style={{ marginTop: '20px' }}>
              <h2 className="financial-creation-section-title">SUMMARY</h2>

              <div className="dashboard-content">
                <div className="cost-section">
                  <CostTable
                    data={summaryData.withBufferMonth}
                    title="Cost with Buffer (Month)"
                    periods={periods}
                  />
                </div>
                <div className="cost-section">
                  <CostTable
                    data={summaryData.withoutBufferMonth}
                    title="Cost Without Buffer (Month)"
                    periods={periods}
                  />
                </div>
                <div className="cost-section">
                  <CostTable
                    data={summaryData.withBufferPeriod}
                    title="Cost With Buffer (Period)"
                    periods={periods}
                  />
                </div>
                <div className="cost-section">
                  <CostTable
                    data={summaryData.withoutBufferPeriod}
                    title="Cost Without Buffer (Period)"
                    periods={periods}
                  />
                </div>
                <div className="cost-section">
                  <CostComparisonTable
                    withData={summaryData.withBufferMonth}
                    withoutData={summaryData.withoutBufferMonth}
                    title="Cost Comparison: With/Without Buffer (per month)"
                    periods={periods}
                  />
                </div>
                <div className="cost-section">
                  <CostComparisonTable
                    withData={summaryData.withBufferPeriod}
                    withoutData={summaryData.withoutBufferPeriod}
                    title="Cost Comparison: With/Without Buffer (per period)"
                    periods={periods}
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      <CommonFooter />
      <ToastContainer position="top-right" autoClose={3000} hideProgressBar={false} />
    </>
  )
}

export default FinancialCreation;